import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { sperre } from "@/hilfe/Knopfhilfe";
import { Label } from "@/components/ui/label";
import SuchAuswahl from "@/components/ui/such-auswahl";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { ArrowLeft, Play } from "lucide-react";
import { listProperties, listEntities, listUnits, listTenants, createTurnover } from "@/lib/api";
import { buildLageMap, propertyDisplay } from "@/lib/propertyLabels";
import { useSchreibschutz } from "@/lib/auth";
import { toast } from "sonner";

const NewTurnover = () => {
    const navigate = useNavigate();
    const [entities, setEntities] = useState([]);
    const [properties, setProperties] = useState([]);
    const [units, setUnits] = useState([]);
    const [tenants, setTenants] = useState([]);

    const [entityId, setEntityId] = useState("");
    const [propertyId, setPropertyId] = useState("");
    const [unitId, setUnitId] = useState("");
    const [oldTenantId, setOldTenantId] = useState("");
    const [newTenantId, setNewTenantId] = useState("");
    const [saving, setSaving] = useState(false);
    const { gesperrt, grund } = useSchreibschutz();

    useEffect(() => {
        (async () => {
            const [e, p, u, t] = await Promise.all([listEntities(), listProperties(), listUnits(), listTenants()]);
            setEntities(e); setProperties(p); setUnits(u); setTenants(t);
        })();
    }, []);

    const filteredProps = useMemo(
        () => entityId ? properties.filter((p) => p.entity_id === entityId) : properties,
        [entityId, properties],
    );
    const lageMap = useMemo(() => buildLageMap(properties, units), [properties, units]);
    const filteredUnits = useMemo(
        () => propertyId
            ? units.filter((u) => u.property_id === propertyId && u.typ === "wohnung")
            : units.filter((u) => u.typ === "wohnung"),
        [propertyId, units],
    );
    const unitTenants = tenants.filter((t) => t.unit_id === unitId);

    const start = async () => {
        if (!unitId) { toast.error("Bitte Mieteinheit wählen"); return; }
        setSaving(true);
        try {
            const t = await createTurnover({
                unit_id: unitId,
                old_tenant_id: oldTenantId || null,
                new_tenant_id: newTenantId || null,
            });
            toast.success("Mieterwechsel gestartet — Einheit ist jetzt in Übergabe");
            navigate(`/wechsel/${t.id}`);
        } finally { setSaving(false); }
    };

    return (
        <div className="px-6 md:px-10 py-8 max-w-2xl">
            <Link to="/wechsel" className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-slate-900">
                <ArrowLeft size={14} /> Zurück
            </Link>
            <div className="mt-4 mb-6">
                <div className="text-[11px] uppercase tracking-[0.2em] text-gray-500 mb-2">Neuer Prozess</div>
                <h1 className="font-heading text-3xl font-bold text-slate-900" data-testid="new-turnover-title">Mieterwechsel starten</h1>
                <p className="text-sm text-gray-500 mt-1">
                    Auswahl-Kaskade: Entität → Immobilie → Mieteinheit. Sobald gestartet, wird die Einheit automatisch auf <b>„In Übergabe"</b> gesetzt.
                </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-md p-6 space-y-5">
                <div>
                    <Label className="mb-1.5 inline-block">Entität (optional)</Label>
                    <SuchAuswahl wert={entityId || "all"} onWaehlen={(v) => { setEntityId(v === "all" ? "" : v); setPropertyId(""); setUnitId(""); }}
                        optionen={[
                            { wert: "all", text: "Alle Entitäten" },
                            ...entities.map((e) => ({ wert: e.id, text: e.name })),
                        ]}
                        testId="new-turnover-entity"
                    />
                </div>

                <div>
                    <Label className="mb-1.5 inline-block">Immobilie *</Label>
                    <SuchAuswahl wert={propertyId} onWaehlen={(v) => { setPropertyId(v); setUnitId(""); }}
                        optionen={[
                            ...filteredProps.map((p) => ({ wert: p.id, text: propertyDisplay(p, lageMap) })),
                        ]}
                        platzhalter="Immobilie wählen"
                        testId="new-turnover-property"
                    />
                </div>

                <div>
                    <Label className="mb-1.5 inline-block">Mieteinheit *</Label>
                    <SuchAuswahl wert={unitId} onWaehlen={setUnitId}
                        optionen={[
                            ...filteredUnits.map((u) => ({ wert: u.id, text: u.unit_label || "Einheit" })),
                        ]}
                        platzhalter="Wohnung wählen"
                        testId="new-turnover-unit"
                    />
                </div>

                <div>
                    <Label className="mb-1.5 inline-block">Alter Mieter (auszuziehend)</Label>
                    <Select value={oldTenantId} onValueChange={setOldTenantId}>
                        <SelectTrigger data-testid="new-turnover-old"><SelectValue placeholder="— optional —" /></SelectTrigger>
                        <SelectContent>
                            {(unitTenants.length ? unitTenants : tenants).map((t) => (
                                <SelectItem key={t.id} value={t.id}>{t.full_name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div>
                    <Label className="mb-1.5 inline-block">Neuer Mieter (einziehend)</Label>
                    <SuchAuswahl wert={newTenantId} onWaehlen={setNewTenantId}
                        optionen={[
                            ...tenants.map((t) => ({ wert: t.id, text: t.full_name })),
                        ]}
                        platzhalter="— optional —"
                        testId="new-turnover-new"
                    />
                </div>

                <div className="pt-3 flex justify-end">
                    <Button onClick={start} disabled={saving || gesperrt}
                            grund={sperre([saving, "Wird gestartet"], [gesperrt, grund])} className="bg-slate-900 hover:bg-slate-800" data-testid="btn-start-turnover">
                        <Play size={14} className="mr-1.5" /> {saving ? "Starte…" : "Prozess starten"}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default NewTurnover;
